import React from 'react';
import { Link } from 'react-router-dom';
import type { TopReferrer } from '../api/types';

export interface TopReferrersProps {
  topReferrers: TopReferrer[] | null;
}

function fmtNum(v: number): string {
  return v.toLocaleString();
}

function fmtCents(v: number): string {
  return (v / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/** `display_name` can be null — fall back to the id, never a blank cell. */
function referrerLabel(row: TopReferrer): string {
  return row.display_name ?? row.user_id;
}

/** One row can list more than one code (a promoter code and the referrer's
 *  own auto-minted personal code both count toward the same person). */
function codesLabel(row: TopReferrer): string {
  return row.codes.length > 0 ? row.codes.join(', ') : '—';
}

/**
 * The promoter leaderboard: referrer, codes, counted, verified, owed. One row
 * per REFERRER — a referrer with attributions through more than one code
 * (e.g. a promoter code AND their auto-minted personal code) still gets
 * exactly one row, with `codes` listing everything that contributed, so the
 * ranking always reflects the person rather than splitting their totals
 * across entries. A null `top_referrers` means the query failed and renders
 * "Not available"; an empty (non-null) array is the honest, different claim
 * that the query ran and nobody referred anyone in this window.
 */
export function TopReferrers({ topReferrers }: TopReferrersProps): React.ReactElement {
  return (
    <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-700">
        <h2 className="text-sm font-semibold text-gray-200">Top referrers</h2>
        <p className="mt-1 text-[11px] text-gray-500">Ranked by referrals counted in this period.</p>
      </div>

      {topReferrers === null ? (
        <div className="py-16 flex items-center justify-center text-sm text-gray-500">
          Not available
        </div>
      ) : topReferrers.length === 0 ? (
        <div className="py-16 flex items-center justify-center text-sm text-gray-500">
          No referrers in this period.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                  Referrer
                </th>
                <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                  Codes
                </th>
                <th className="px-5 py-3 text-right font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                  Counted
                </th>
                <th className="px-5 py-3 text-right font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                  Verified
                </th>
                <th className="px-5 py-3 text-right font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                  Owed
                </th>
              </tr>
            </thead>
            <tbody>
              {topReferrers.map((row) => (
                <tr
                  key={row.user_id}
                  className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                >
                  <td className="px-5 py-3">
                    <Link
                      to={`/analytics/referrals/users/${row.user_id}`}
                      className="text-gray-200 hover:text-violet-300 transition-colors font-medium"
                    >
                      {referrerLabel(row)}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-gray-400">{codesLabel(row)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-gray-200">
                    {fmtNum(row.counted)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-gray-400">
                    {fmtNum(row.verified)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-gray-200">
                    {fmtCents(row.owed_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default TopReferrers;
